
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { Review, ServiceVisit } from '@/types';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { StarRating } from './StarRating';

interface ReviewFormProps {
  visit: ServiceVisit;
  onReviewSubmitted: (review: Review) => void;
}

const ReviewForm = ({ visit, onReviewSubmitted }: ReviewFormProps) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [rating, setRating] = useState<number>(0);
  const [comment, setComment] = useState<string>('');
  const [tipAmount, setTipAmount] = useState<number>(0);
  const [submitting, setSubmitting] = useState<boolean>(false);
  
  const tipOptions = [0, 5, 10, 15, 20, 25];

  const handleSubmit = async () => {
    if (!user || rating === 0) return;
    
    setSubmitting(true);
    
    try {
      const reviewData = {
        visitid: visit.id,
        userid: user.id,
        technicianid: visit.technicianId || '',
        rating,
        comment: comment.trim() || null,
        timestamp: new Date().toISOString(),
        tipamount: tipAmount || null
      };
      
      const { data, error } = await supabase
        .from('reviews')
        .insert(reviewData)
        .select()
        .maybeSingle();
      
      if (error) throw error;
      
      // Update the visit with the review reference
      await supabase
        .from('service_visits')
        .update({ review: data.id })
        .eq('id', visit.id);
      
      // If there's a tip, we would process the payment here
      if (tipAmount > 0) {
        // In a real app, this would integrate with a payment processor
        console.log(`Processing tip: $${tipAmount}`);
      }
      
      toast({
        title: "Thank you for your feedback!",
        description: "Your review has been submitted successfully.",
      });
      
      onReviewSubmitted(data as unknown as Review);
    } catch (error) {
      console.error('Error submitting review:', error);
      toast({
        title: "Error",
        description: "There was a problem submitting your review. Please try again.",
        variant: "destructive"
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-medium mb-2">Rate your service</h3>
        <p className="text-sm text-muted-foreground mb-4">
          How would you rate your plant care service with {visit.technicianName}?
        </p>
        <StarRating 
          maxStars={5}
          value={rating}
          onChange={setRating}
          size="large"
        />
      </div>
      
      <div>
        <h3 className="text-lg font-medium mb-2">Leave a comment (optional)</h3>
        <Textarea
          placeholder="Share your experience with this service..."
          className="resize-none"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
        />
      </div>
      
      <div>
        <h3 className="text-lg font-medium mb-2">Add a tip (optional)</h3>
        <div className="flex flex-wrap gap-2">
          {tipOptions.map((amount) => (
            <Button
              key={amount}
              type="button"
              variant={tipAmount === amount ? "default" : "outline"}
              onClick={() => setTipAmount(amount)}
              className="flex-1"
            >
              {amount === 0 ? "No Tip" : `$${amount}`}
            </Button>
          ))}
        </div>
      </div>
      
      <Button
        className="w-full"
        disabled={rating === 0 || submitting}
        onClick={handleSubmit}
      >
        {submitting ? "Submitting..." : "Submit Review"}
      </Button>
    </div>
  );
};

export default ReviewForm;
